import {
  DayIntervalUnit,
  formatDateToMMDDYYYY,
  RoutineWorkflow,
  useCreateMutation,
  useDeleteMutation,
  useGetQuery,
  useRoutineAlerts,
  useUpdateMutation,
  useWorkspaceRoutine,
  WorkspaceRoutine,
} from '@gripp/shared-logic';
import {
  AlertForm,
  AlertType,
  excludedConfigKeys,
  getAlertType,
  getCounterRoutinesForOnInterval,
  Icon,
  ListCountProvider,
  LoadingIndicator,
  SlideAction,
  SlideLayout,
  tokens,
  ViewMode,
  type ColumnData,
} from '@gripp/shared-ui';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { AlertsList } from './alertsList';

export type AlertsProps = {
  /** Override productId instead of reading from search params. */
  productId?: string;
  /** Base path for alert routes (e.g. "/products/123/alerts"). Defaults to "/alerts". */
  basePath?: string;
  /** Path for the left-column back button. When set, a back button is shown. */
  backPath?: string;
  /** Workspace ID — required for product alert creation. */
  workspaceId?: string;
};

export const Alerts = ({
  productId: productIdProp,
  basePath: basePathProp,
  backPath,
  workspaceId,
}: AlertsProps = {}) => {
  const { routineId, alertId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const workspaceAssetId = searchParams.get('workspaceAssetId') || '';
  const productId = productIdProp ?? searchParams.get('productId') ?? undefined;
  const groupId = searchParams.get('groupId') || undefined;
  const basePath = basePathProp ?? '/alerts';
  const { t } = useTranslation();

  const locationState = location.state as Record<string, unknown> | null;
  const initialTransition =
    locationState?.slideLayoutInitialTransition === SlideAction.NEXT ||
    locationState?.slideLayoutInitialTransition === SlideAction.PREV
      ? (locationState.slideLayoutInitialTransition as SlideAction)
      : undefined;

  // Detect new/edit mode from URL pathname (works for any base path)
  const isNew = location.pathname.endsWith('/new');
  const isEdit = !!alertId && !isNew;
  // Show right column if we have a routineId and we're either creating or editing
  const showRightColumn = (isEdit || isNew) && !!routineId;

  const routine = useWorkspaceRoutine(routineId || '');
  const { workspaceAsset, isLoading: isAssetLoading } = useRoutineAlerts(
    workspaceAssetId,
    routineId || '',
    productId
  );

  const { data: workflowData, isLoading: isWorkflowLoading } = useGetQuery({
    modelName: 'workflow',
    query: GET_WORKFLOW,
    id: alertId || '',
    options: { enabled: !!alertId, refetchOnMount: true },
  });

  const { mutateAsync: createWorkflow } = useCreateMutation({
    modelName: 'workflow',
    query: CREATE_WORKFLOW,
  });

  const { mutateAsync: updateWorkflow } = useUpdateMutation({
    modelName: 'workflow',
    query: UPDATE_WORKFLOW,
  });

  const { mutateAsync: deleteWorkflow } = useDeleteMutation({
    modelName: 'workflow',
    query: DELETE_WORKFLOW,
  });

  const [defaultObject, setDefaultObject] = useState<any>(null);
  const [counterRoutines, setCounterRoutines] = useState<WorkspaceRoutine[]>(
    []
  );
  const [workflow, setWorkflow] = useState<RoutineWorkflow>();
  const alertFormRef = useRef<{
    handleSubmit: (fn: (data: any) => Promise<void>) => () => void;
    reset: () => void;
  } | null>(null);

  const isLoading = isAssetLoading || (!!alertId && isWorkflowLoading);

  const alertsListPath = useMemo(() => {
    // When using a custom basePath (e.g. product routes), no query params needed
    if (basePathProp) return `${basePath}/${routineId}`;
    const params = new URLSearchParams();
    if (workspaceAssetId) params.set('workspaceAssetId', workspaceAssetId);
    if (productId) params.set('productId', productId);
    if (groupId) params.set('groupId', groupId);
    const qs = params.toString();
    return `${basePath}/${routineId}${qs ? `?${qs}` : ''}`;
  }, [routineId, workspaceAssetId, productId, groupId, basePath, basePathProp]);

  // Ref to compare last applied edit defaults (avoids defaultObject in effect deps → no loop)
  const lastAppliedEditRef = useRef<{
    workflowId: string;
    defaults: Record<string, unknown>;
  } | null>(null);

  const defaultValuesShallowEqual = (
    a: Record<string, unknown>,
    b: Record<string, unknown>
  ) => {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    for (const k of keys) {
      if (a[k] !== b[k]) return false;
    }
    return true;
  };

  // Load default values for edit mode. Skip only when same workflow and same data (use ref so we don't depend on defaultObject → no loop).
  useEffect(() => {
    if (!alertId) {
      lastAppliedEditRef.current = null;
      return;
    }
    if (!workflowData || isLoading) return;
    const routineWorkflow = workflowData as RoutineWorkflow;
    if (!routineWorkflow?.trigger?.config) return;

    const configKeys = Object.keys(routineWorkflow.trigger.config);
    const alertKeys = configKeys.filter(
      (key) => !excludedConfigKeys.includes(key)
    );
    const alertType = getAlertType(alertKeys[0]);
    const secondAlertType =
      (alertKeys.length > 1 && getAlertType(alertKeys[1])) || undefined;

    const defaultValues = {
      alertLabel: routineWorkflow?.name || '',
      alertLabelPlaceholder: routineWorkflow?.name || '',
      startDate: formatDateToMMDDYYYY(routineWorkflow?.disabledBetween?.start),
      endDate: formatDateToMMDDYYYY(routineWorkflow?.disabledBetween?.end),
      alertType: alertType,
      secondAlertType: secondAlertType,
      inputField: getInputValue(alertType, routineWorkflow),
      secondInputField: getInputValue(secondAlertType, routineWorkflow),
      minValue: routineWorkflow?.trigger?.config?.ceiling,
      maxValue: routineWorkflow?.trigger?.config?.floor,
      completions:
        routineWorkflow?.trigger?.config?.onCompletionCount?.toString(),
      daysSkipped:
        routineWorkflow?.trigger?.config?.daysSinceCompleted?.toString(),
      onInterval: routineWorkflow?.trigger?.config?.onInterval?.toString(),
      onIntervalStart:
        routineWorkflow?.trigger?.config?.onIntervalStart?.toString(),
      intervalRoutineId:
        routineWorkflow?.trigger?.config?.triggerCounterRoutineId,
      onDate: formatDateToMMDDYYYY(routineWorkflow?.trigger?.config?.onDate),
      dayInterval: routineWorkflow?.trigger?.config?.dayInterval,
      dayIntervalUnit: routineWorkflow?.trigger?.config?.dayIntervalUnit,
      daysStartDate: formatDateToMMDDYYYY(
        routineWorkflow?.trigger?.config?.startDate
      ),
    };

    const prev = lastAppliedEditRef.current;
    if (
      prev?.workflowId === routineWorkflow?.id &&
      defaultValuesShallowEqual(defaultValues, prev.defaults)
    ) {
      return; // same workflow and same data; skip (workflowData is new ref every time from query)
    }

    const counterRoutines = workspaceAsset
      ? getCounterRoutinesForOnInterval(workspaceAsset)
      : [];
    setCounterRoutines(counterRoutines);
    setDefaultObject(defaultValues);
    setWorkflow(routineWorkflow);
    lastAppliedEditRef.current = {
      workflowId: routineWorkflow.id,
      defaults: defaultValues,
    };
  }, [alertId, workflowData, isLoading, workspaceAsset]);

  // Load default values for create mode - use useMemo to ensure stable reference
  const createModeDefaultValues = useMemo(() => {
    if (alertId || isLoading || !routine) return null;

    const routineName = routine?.config?.routineButtonText;
    const daysStartDate = moment().format('MM/DD/YYYY');

    return {
      selectedRoutine: routineId,
      alertLabel: '',
      alertLabelPlaceholder: `${t('alerts.alert')}: ${routineName}`,
      startDate: undefined,
      endDate: undefined,
      minValue: undefined,
      maxValue: undefined,
      alertType: undefined,
      secondAlertType: undefined,
      days: undefined,
      daysStartDate: daysStartDate,
      completions: undefined,
      daysSkipped: undefined,
      onDate: undefined,
      dayInterval: 1,
      dayIntervalUnit: DayIntervalUnit.Days,
      intervalRoutineId: undefined,
      onInterval: undefined,
      onIntervalStart: undefined,
    };
  }, [alertId, isLoading, routine, routineId, t]);

  // Set defaultObject once when createModeDefaultValues is ready. Use stable deps only (no defaultObject)
  // so setting state doesn't re-trigger the effect and cause maximum update depth.
  useEffect(() => {
    if (createModeDefaultValues && !defaultObject) {
      const counterRoutines = workspaceAsset
        ? getCounterRoutinesForOnInterval(workspaceAsset)
        : [];
      setCounterRoutines(counterRoutines);
      setDefaultObject(createModeDefaultValues);
    }
  }, [createModeDefaultValues, workspaceAsset?.id]);

  const getInputValue = (
    alertType: AlertType | undefined,
    workflow: RoutineWorkflow
  ) => {
    if (!alertType || !workflow) return undefined;
    switch (alertType) {
      case AlertType.DaysSkipped:
        return workflow?.trigger?.config?.daysSinceCompleted?.toString();
      case AlertType.RoutineCompletions:
        return workflow?.trigger?.config?.onCompletionCount?.toString();
      case AlertType.Min:
        return workflow?.trigger?.config?.ceiling?.toString();
      case AlertType.Max:
        return workflow?.trigger?.config?.floor?.toString();
      default:
        return undefined;
    }
  };

  const submitAlert = useCallback(
    async (input: any) => {
      if (alertId) {
        await updateWorkflow(
          { input: { ...input, id: alertId } },
          { onSuccess: async () => navigate(alertsListPath) }
        );
      } else {
        if (productId) {
          input.product = { id: productId };
          if (workspaceId) input.workspace = { id: workspaceId };
          delete input.workspaceAsset;
        }
        if (groupId) {
          input.group = { id: groupId };
          delete input.workspaceAsset;
        }
        await createWorkflow(
          { input },
          { onSuccess: async () => navigate(alertsListPath) }
        );
      }
    },
    [
      alertId,
      navigate,
      alertsListPath,
      productId,
      groupId,
      updateWorkflow,
      createWorkflow,
    ]
  );

  const deleteAlert = async () => {
    if (!workflow) return;
    await deleteWorkflow(
      { input: { id: workflow.id } },
      {
        onSettled: () => {
          navigate(alertsListPath);
        },
      }
    );
  };

  const onClose = () => {
    navigate(alertsListPath);
  };

  const [count, setCount] = useState<number>();
  const leftColumn: ColumnData = {
    title: 'alerts.alerts',
    count,
    mainComponent: (
      <AlertsList
        routineId={routineId || ''}
        onItemsCountChange={setCount}
        basePath={basePathProp}
        productId={productIdProp}
        workspaceAssetId={workspaceAssetId || undefined}
      />
    ),
    disableScroll: true,
    ...(backPath
      ? {
          showBackButton: true,
          backButtonTransition: SlideAction.PREV,
          backButtonTransitionBehavior: 'concurrent' as const,
          backButtonTransitionExitColumn: 'both' as const,
          onBackButtonPress: () =>
            navigate(backPath, {
              state: { slideLayoutInitialTransition: SlideAction.PREV },
            }),
        }
      : {}),
  };

  const renderRightColumn = () => {
    const isNewAlert = !alertId;
    // Edit: only use defaultObject after workflow has loaded (edit effect sets both), so first open shows correct values
    const formDefaults = isNewAlert
      ? createModeDefaultValues
      : workflow && defaultObject
      ? defaultObject
      : null;
    if (isLoading || !routine || !formDefaults) {
      return (
        <View style={styles.loadingContainer}>
          <LoadingIndicator
            backgroundColor={tokens.colors.neutral[100]}
            flexContainer
          />
        </View>
      );
    }

    // For edit mode: key by alertId so form remounts with correct defaultValues; confirm/reset then work.
    if (!isNewAlert) {
      return (
        <View style={styles.editFormWrapper}>
          <AlertForm
            key={alertId}
            formRef={alertFormRef}
            defaultValues={defaultObject}
            routine={routine}
            workspaceAsset={workspaceAsset}
            product={productId ? { id: productId } : undefined}
            group={groupId ? { id: groupId } : undefined}
            workflowProps={workflow}
            counterRoutines={counterRoutines}
            submitAlert={submitAlert}
            deleteAlert={deleteAlert}
            onClose={onClose}
            isEdit={true}
            headerTitle="alerts.editAlert"
            backButtonLabel="topNav.back"
            openDropdownOnLoad={false}
          />
        </View>
      );
    }

    // New alert: use create defaults and key="new" so after edit→back→new we get a fresh form (only alert logic picker).
    return (
      <View style={styles.formContainer}>
        <AlertForm
          key="new"
          formRef={alertFormRef}
          defaultValues={createModeDefaultValues}
          routine={routine}
          workspaceAsset={workspaceAsset}
          product={productId ? { id: productId } : undefined}
          group={groupId ? { id: groupId } : undefined}
          workflowProps={workflow}
          counterRoutines={counterRoutines}
          submitAlert={submitAlert}
          deleteAlert={undefined}
          onClose={onClose}
          isEdit={false}
          headerTitle="alerts.newAlert"
          backButtonLabel="topNav.back"
          openDropdownOnLoad={true}
        />
      </View>
    );
  };

  // Only pass rightColumn when showing (SlideLayout expects undefined, not null)
  const rightColumn: ColumnData | undefined = showRightColumn
    ? {
        title: isEdit ? 'alerts.editAlert' : 'alerts.alerts',
        mainComponent: renderRightColumn(),
        showBackButton: true,
        backButtonTransition: SlideAction.SINGLE,
        onBackButtonPress: () => {
          navigate(alertsListPath);
        },
        disableScroll: true, // Form handles its own scrolling
        headerComponent: (
          <Pressable
            style={styles.headerIconButton}
            onPress={() => alertFormRef.current?.reset()}
            accessibilityLabel={t('versionCheck.refreshButton')}
          >
            <Icon name="Reset" size={24} color={tokens.colors.neutral[800]} />
          </Pressable>
        ),
      }
    : undefined;

  return (
    <ListCountProvider count={count}>
      <SlideLayout
        leftColumn={leftColumn}
        rightColumn={rightColumn}
        viewMode={showRightColumn ? ViewMode.DUAL : ViewMode.SINGLE}
        initialTransition={initialTransition}
      />
    </ListCountProvider>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    backgroundColor: tokens.colors.neutral[100],
  },
  headerIconButton: {
    padding: 8,
  },
  editFormWrapper: {
    flex: 1,
    backgroundColor: tokens.colors.neutral[100],
    height: '100%',
    minHeight: '100%',
  },
  formContainer: {
    flex: 1,
    backgroundColor: tokens.colors.neutral[100],
    height: '100%',
  },
});

const GET_WORKFLOW = `
  query GetWorkflow($id: ID!) {
    getWorkflow(id: $id) {
      id
      name
      group {
        id
      }
      product {
        id
      }
      workspaceAsset {
        id
      }
      trigger {
        type
        config
      }
      action {
        type
        config
      }
      disabled
      disabledBetween {
        start
        end
      }
      workspace {
        id
      }
    }
  }
`;

const CREATE_WORKFLOW = `mutation createWorkflow($input: CreateWorkflowInput!) {
  createWorkflow(input: $input) {
    id
  }
}`;

const UPDATE_WORKFLOW = `
  mutation UpdateWorkflow($input: MutateWorkflowInput!) {
    updateWorkflow(input: $input) {
      id
    }
  }
`;

const DELETE_WORKFLOW = `
  mutation DeleteWorkflow($input: MutateWorkflowInput!) {
    deleteWorkflow(input: $input) {
      id
    }
  }
`;
